import React, { useState, useCallback, useMemo, useContext } from 'react';
import { SORT_BY_STRING } from '@ubeya/shared/utils/sorting';
import styled from 'styled-components';
import DropDown from '../../DropDown';
import CheckBox from '../../CheckBox';
import FilterLayout from '../FilterLayout';
import ConfigContext from '../../../contexts/ConfigContext';
import { TextWithTooltip } from '../../Tooltip';

const GenericFilterText = styled(TextWithTooltip)`
  max-width: 200px;
`;

const GenericFilter = ({
  slug,
  defaultValue,
  handleClose,
  onApply,
  search,
  onChange,
  externalOptions = {},
  hideLayout = false,
  titleKey,
  namePrefix,
  staticOptions = null
}) => {
  const { t } = useContext(ConfigContext);
  const [values, setValues] = useState(defaultValue || []);

  // Use static options if provided, otherwise get from externalOptions
  const rawOptions = useMemo(() => staticOptions || externalOptions[slug] || [], [
    staticOptions,
    externalOptions,
    slug
  ]);

  const options = useMemo(() => {
    if (staticOptions) {
      return staticOptions;
    }

    return rawOptions
      .map(({ id, value }) => ({
        value: id,
        label: value
      }))
      .sort(SORT_BY_STRING('label'));
  }, [rawOptions, staticOptions]);

  const handleChange = useCallback(
    (value) => {
      const newValues = values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
      setValues(newValues);
      onChange?.(newValues);
    },
    [onChange, values]
  );

  const filteredOptions = useMemo(
    () =>
      search
        ? (options || []).filter(
            ({ label }) =>
              t(titleKey).toLowerCase().includes(search.toLowerCase()) ||
              label.toLowerCase().includes(search.toLowerCase())
          )
        : options,
    [options, search, t, titleKey]
  );

  if (!filteredOptions.length) {
    return null;
  }

  return (
    <FilterLayout name={t(titleKey)} handleClose={handleClose} onApply={() => onApply(values)} hideLayout={hideLayout}>
      {filteredOptions.map(({ value, label }) => (
        <DropDown.Item key={value}>
          <CheckBox name={`${namePrefix}-${value}`} value={values.includes(value)} onChange={() => handleChange(value)}>
            <GenericFilterText>{label}</GenericFilterText>
          </CheckBox>
        </DropDown.Item>
      ))}
    </FilterLayout>
  );
};

export default GenericFilter;
